'use client';

import { cn } from '@/lib/tailwind';
import { UiIcon } from '@/components/ui/UiIcon';
import UiLogo from '@/components/ui/UiLogo';
import { useScrolled } from '@/hooks';
import Link from 'next/link';
import { NavigationMeta } from '@/features/navigation';
import { useState } from 'react';
import dynamic from 'next/dynamic';

// Only phones ever open this panel, and it pulls in `UiModal` with the
// `@headlessui/react` dialog, so it loads on demand. `ssr: false` inside this
// `'use client'` module is what actually moves the bytes — a runtime condition
// would not (see AGENTS.md → Performance).
//
// Note: the dialog chunk is still fetched eagerly on pages with a form, because
// the form fields import `Input`/`Select`/`Textarea` from the same package.
// Moving those to native elements would drop it from the eager set entirely.
const NavigationMainModal = dynamic(
    () => import('@/features/navigation/NavigationMainModal').then((m) => m.NavigationMainModal),
    { ssr: false },
);

interface HeaderProps {
    className?: string;
    /**
     * If true, the header is transparent when at the very top and becomes solid after scrolling.
     * Intended for the homepage only.
     */
    transparentAtTop?: boolean;
    /**
     * Controls whether the header overlays content (fixed) or takes space in the layout (sticky).
     */
    position?: 'sticky' | 'fixed';
}

function Header({ className, transparentAtTop = false, position = 'sticky' }: HeaderProps) {
    const isScrolled = useScrolled();
    const showTransparent = transparentAtTop && !isScrolled;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // Mounting the lazy panel is what triggers its request, so it is gated
    // separately from `isMenuOpen`: pointer/focus on the trigger loads the chunk,
    // the click then only flips the panel open.
    const [isMenuMounted, setIsMenuMounted] = useState(false);

    return (
        <>
            <header
                className={cn(
                    position === 'fixed' ? 'fixed inset-x-0 top-0 z-50' : 'sticky inset-x-0 top-0 z-50',
                    'flex flex-col justify-center',
                    'py-3',
                    'border-b border-solid border-white/20',
                    'transition-[background-color] duration-500 ease-in-out',
                    showTransparent ? 'bg-transparent' : 'bg-body',
                    className,
                )}
            >
                <div className="mx-auto w-full max-w-page-xl px-gutter">
                    <div className="grid grid-cols-2 gap-6 items-center">
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsMenuMounted(true);
                                    setIsMenuOpen(true);
                                }}
                                onPointerEnter={() => setIsMenuMounted(true)}
                                onFocus={() => setIsMenuMounted(true)}
                                aria-label="Open menu"
                                aria-haspopup="dialog"
                                aria-expanded={isMenuOpen}
                                className={cn(
                                    'inline-flex items-center justify-center',
                                    'size-11 -ml-2',
                                    'text-white',
                                    'cursor-pointer',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
                                )}
                            >
                                <UiIcon name="menu" aria-hidden="true" className="w-[22px] h-[16px]" />
                            </button>

                            <Link href="/" aria-label="Homepage" className="inline-block">
                                <UiLogo variant="emblem" className="size-9" />
                            </Link>
                        </div>

                        <NavigationMeta />
                    </div>
                </div>
            </header>

            {isMenuMounted ? <NavigationMainModal open={isMenuOpen} onClose={() => setIsMenuOpen(false)} /> : null}
        </>
    );
}

export default Header;
